<template>
  <div>
    <transition name="fade" mode="out-in">
      <div v-if="selected" :class="['join-values', {'not-selected' : !activeSelected}]">
        <div v-if="index > 0" :class="['join-button', 'and', {'selected': element.joinValue === 'and'}]" @click="changeQuestionItemJoinValue('and', index)">{{ getLang('and') }}</div>
        <div v-if="index > 0" :class="['join-button', 'or', {'selected': element.joinValue === 'or'}]" @click="changeQuestionItemJoinValue('or', index)">{{ getLang('or') }}</div>
        <div :class="['join-button', 'minus', {'selected': element.joinValue === 'minus'}]" @click="changeQuestionItemJoinValue('minus', index)">{{ getLang('minus') }}</div>
      </div>
    </transition>
    <div :class="['question-item', {'with-join-values': selected && index > -1}, {'description-open': descriptionOpen}, {'indicator': isIndicator}]">
      <i v-if="element.subtype === 'domain'" class="sherpa-icon icon-lista item-icon"></i>
      <i v-else-if="element.subtype === 'text'" class="sherpa-icon icon-editar item-icon"></i>
      <i v-else-if="element.subtype === 'number'" class="fas fa-sort-numeric-down item-icon" style="margin-top: 3px;"></i>
      <i v-else-if="element.subtype === 'date'" class="sherpa-icon icon-calendario item-icon"></i>
      <i v-else class="sherpa-icon icon-preguntes item-icon"></i>
      <div class="question-description">
        <div class="name">
          <span v-html="highlightText(element.name)" />
        </div>
        <span class="description id" v-html="highlightText(element.indexField)"></span>
        <span :class="['description', 'type', {'active-description': activeSelected}]" @click.stop="descriptionToggle">
          <template v-if="selected">
            <transition name="fade-slide-left" mode="out-in">
              <spinner-loading v-if="loadingDomain" class="spinner-loading" :line-size='2' line-fg-color='#076cfabe' :size="12" />
              <i v-else class="fas fa-chevron-circle-down fa-sm"></i>
            </transition>
          </template>
          <template v-if="selected">{{ getLang('conditionsOf') }}</template>
          <span :class="{'type-selected': selected}">{{ getTypeName(element) }}</span>
          <template v-if="selected">
            <transition name="fade-slide-right" mode="out-in">
              <div v-if="conditionResume" class="condition-resume" v-html="conditionResume" />
            </transition>
          </template>
        </span>
      </div>
      <template v-if="selected">
        <div class="edition-element">
          <transition name="fade-slide-right" mode="out-in">
            <div v-if="selected && element.active && totalRows !== null" class="total-rows">
              <span class="total-label" key="total-label"><i :class="['fas', 'fa-sort-numeric-down', {'loading': totalRowsLoading}]"></i> {{ getLang('totalQuestionItemRows') }}</span>
              <transition name="fade-slide-left" mode="out-in">
                <span v-if="!totalRowsLoading" class="total" key="total">{{ formatNumber(totalRows) }}</span>
                <spinner-loading class="spinner-loading" v-else :line-size='2' line-fg-color='#5dbecd' key="total-loading" />
              </transition>
            </div>
          </transition>
          <toggle-button
            v-model="element.active"
            :sync="true"
            :labels="{checked: getLang('active'), unchecked: getLang('inactive')}"
            :width="80"
            :font-size="11"
            :margin="3"
            :speed="200"
            :color="{checked: '#4ab800c5', unchecked: '#a6a6a6', disabled: '#efefef'}"
            @change="activeChange"
          />
          <i class="sherpa-icon icon-trash" @click="deleteElement"></i>
        </div>
        <div v-if="activeSelected && element.operation"
          :class="['description-data',
            {'show-responses': showDomainResponses}, {'show-text-value': showTextValueSection}, {'show-number-value': showNumberValueSection},
            {'show-date-value': showDateValueSection}
          ]"
        >
          <transition name="fade" mode="out-in">
            <domain-data v-if="element.subtype === 'domain'" v-show="domainLoaded"
              :key="'domain_' + element.id"
              :element="element"
              :domainEndpoint="domainEndpoint"
              :currentLanguage="currentLanguage"
              :segmentId="segmentId"
              :getLang="getLang"
              :selected="selected"
              :openDomain="openDomain"
              :sendConditionResume="sendConditionResume"
              @loadingDomain="loadingDomainEvent"
              @domainLoaded="domainLoadedEvent"
              @showResponses="showValueEvent"
              @conditionResumeSet="conditionResumeEvent"
            />
            <text-data v-else-if="element.subtype === 'text'"
              :key="'text_' + element.id"
              :element="element"
              :getLang="getLang"
              :selected="selected"
              :sendConditionResume="sendConditionResume"
              @textLoaded="elementLoadedEvent"
              @showTextValue="showValueEvent"
              @conditionResumeSet="conditionResumeEvent"
            />
            <number-data v-else-if="element.subtype === 'number'"
              :key="'number_' + element.id"
              :element="element"
              :getLang="getLang"
              :selected="selected"
              :sendConditionResume="sendConditionResume"
              @numberLoaded="elementLoadedEvent"
              @showNumberValue="showValueEvent"
              @conditionResumeSet="conditionResumeEvent"
            />
            <date-data v-else-if="element.subtype === 'date'"
              :key="'date_' + element.id"
              :element="element"
              :getLang="getLang"
              :selected="selected"
              :sendConditionResume="sendConditionResume"
              @dateLoaded="elementLoadedEvent"
              @showDateValue="showValueEvent"
              @openCalendar="openCalendar"
              @conditionResumeSet="conditionResumeEvent"
            />
          </transition>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import domainData from './domainData'
import textData from './textData'
import numberData from './numberData'
import dateData from './dateData'
import debounce from 'lodash/debounce'
import ToggleButton from '@/components/shared/ToggleButton.vue'

export default {
  name: 'question-item',
  components: {
    domainData,
    textData,
    numberData,
    dateData,
    ToggleButton
  },
  props: {
    element: Object,
    parent: Object,
    parentChildren: Array,
    getTypeName: Function,
    getLang: Function,
    selected: Boolean,
    index: Number,
    domainEndpoint: String,
    currentLanguage: String,
    segmentId: String,
    getRowsNumber: Function,
    formatNumber: Function,
    changeJoinValue: Function,
    highlightTerm: { type: String, default: '' }
  },
  data () {
    return {
      loadingDomain: false,
      descriptionOpen: false,
      domainLoaded: false,
      showValue: true,
      openDomain: false,
      conditionResume: null,
      totalRowsLoading: false,
      totalRows: null
    }
  },
  methods: {
    highlightText (text) {
      if (!text || !this.highlightTerm) return text || ''
      const norm = s => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
      const normText = norm(text)
      const normTerm = norm(this.highlightTerm)
      if (!normTerm) return text
      let result = ''
      let i = 0
      while (i < text.length) {
        if (normText.substring(i, i + normTerm.length) === normTerm) {
          result += '<mark>' + text.substring(i, i + normTerm.length) + '</mark>'
          i += normTerm.length
        } else {
          result += text[i]
          i++
        }
      }
      return result
    },
    deleteElement () {
      this.$emit('deleteElementCall', this.element)
    },
    descriptionToggle () {
      if (this.descriptionOpen) {
        this.descriptionOpen = false
      } else if (this.element.subtype !== 'domain' || this.domainLoaded) {
        this.descriptionOpen = true
      } else if (this.element.subtype === 'domain') {
        this.openDomain = true
      }
    },
    loadingDomainEvent (loadingDomain) {
      this.loadingDomain = loadingDomain
    },
    domainLoadedEvent () {
      this.loadingDomain = false
      this.domainLoaded = true
      if (this.element.recentlyAdded) {
        this.descriptionOpen = true
      }
    },
    elementLoadedEvent () {
      if (this.element.recentlyAdded) {
        this.descriptionOpen = true
      }
    },
    showValueEvent (value) {
      this.showValue = value
    },
    openCalendar (value) {
    },
    conditionResumeEvent (value) {
      this.conditionResume = value
    },
    getOperationByKey (key, operations) {
      return operations.find(op => op.key === key) || null
    },
    getOperationName (key, operations, getValuesStr) {
      const op = this.getOperationByKey(key, operations)
      return op ? '<strong>' + op.name + '</strong>' + getValuesStr() : ''
    },
    getAggregateName (fn) {
      if (!fn) return null
      const map = {SUM: 'aggFuncSum', AVG: 'aggFuncAvg', MAX: 'aggFuncMax', MIN: 'aggFuncMin', MEDIAN: 'aggFuncMedian'}
      const key = map[String(fn).toUpperCase()]
      return key ? this.getLang(key) : null
    },
    sendConditionResume (component) {
      if (component.element.operation) {
        let resume = this.getOperationName(component.element.operation.operationValue, component.operations, component.getValuesStr)
        const aggName = this.getAggregateName(component.element.operation.aggregateFunction)
        if (aggName && resume) {
          resume = '<strong>' + aggName + '</strong> ' + resume
        }
        component.$emit('conditionResumeSet', resume)
      } else {
        component.$emit('conditionResumeSet', null)
      }
    },
    rowsCount () {
      if (this.selected && (this.element.subtype !== 'domain' || this.element.operation.domain)) {
        const newElement = JSON.parse(JSON.stringify(this.element))
        newElement.joinValue = ''
        this.getRowsNumber([newElement], this)
        this.$emit('newRowsCount')
      }
    },
    changeQuestionItemJoinValue (value, index) {
      if (this.element.active) {
        this.changeJoinValue(this, value, index)
      }
    },
    activeChange (toggle) {
      if (toggle.value === true) {
        this.$emit('propagateActive', this.element)
      } else {
        this.$emit('propagateInactive')
      }
    }
  },
  mounted () {
  },
  computed: {
    activeSelected () {
      return this.selected && this.element.active
    },
    showDomainResponses () {
      return this.showValue && this.element.subtype === 'domain'
    },
    showTextValueSection () {
      return this.showValue && this.element.subtype === 'text'
    },
    showNumberValueSection () {
      return this.showValue && this.element.subtype === 'number'
    },
    showDateValueSection () {
      return this.showValue && this.element.subtype === 'date'
    },
    elementOperationFingerprint () {
      return JSON.stringify(this.element.operation)
    },
    elementJoinValueFingerprint () {
      return JSON.stringify(this.element.joinValue)
    },
    elementActiveFingerprint () {
      return JSON.stringify(this.element.active)
    },
    isIndicator () {
      if (this.element && this.element.indexField) {
        return this.element.indexField.includes('indicators.')
      }
      return false
    }
  },
  watch: {
    elementOperationFingerprint: {
      immediate: true,
      handler () {
        // `this` está definido correctamente aquí
        debounce(() => {
          this.rowsCount()
        }, 1000)()
      }
    },
    'element.active': function (newVal, oldVal) {
      debounce(() => {
        if (!newVal) {
          this.totalRowsLoading = false
          this.totalRows = null
        } else {
          this.rowsCount()
        }
      }, 1000)()
    },
    elementJoinValueFingerprint () {
      this.$emit('newJoinValue')
    }
  }
}
</script>

<style lang="scss">
.segment-builder .question-item {
  position: relative;
  transition: all 0.2s ease;
  padding: 12px 13px 8px;

  &.with-join-values {
    padding-top: 42px;
  }



  mark {
    background-color: #fff176;
    color: inherit;
    border-radius: 2px;
    padding: 0 1px;
  }

  .name {
    display: inline-block;
    background-color: transparent;
    border: 1px solid transparent;
    border-radius: 2px;
    padding: 1px 6px 1.5px 3px;
    position: relative;
    margin: 1px 0 0 1px;
  }

  .condition-resume {
    display: inline-block;
    margin-left: 5px;
    margin-right: 5px;
    margin-top: 2px;
    font-style: italic;
    padding: 1px 4px 0;
    background-color: #fff;
  }

  .description {
    border: 1px dotted transparent;

    i {
      vertical-align: inherit !important;
      font-size: 12.5px;
      color: #9f9f9f;
      margin-left: 2px;
      transition: all 0.2s ease;
    }
  }

  .active-description {
    cursor: pointer;
    transition: all 0.2s ease;
    color: #076cfabe !important;
    border-radius: 2px;
    border: 1px dotted #999;
    background-color: #e4fffe;

    &:hover {
      border: 1px dotted #222;

      i {
        color: #076cfabe;
      }
    }

    i {
      color: #076cfabe;
    }

    .spinner-loading {
      display: inline-block;
      margin-left: 2px;
      padding-top: 2px;
      padding-right: 5px;
      vertical-align: text-top;
    }

    .condition-resume {
      color: #269809;
    }
  }

  .total-rows {
    height: 26px;
    padding: 5px 7px;
    display: inline-flex;
    align-items: center;
    border-radius: 3px;
    border: 1px solid #269809;
    transition: all 0.2s ease;
    background-color: #ecffe6;
    transition: all 0.2s ease;
    margin-right: 10px;

    .total-label,
    .total {
      color: #269809;
      font-family: 'Source Sans Pro';
      font-size: 13px;
      display: inline-block;
    }

    .total-label {
      margin-right: 6px;

      i {
        font-size: 12px;
        margin-right: 1px;
        color: #269809;
        vertical-align: baseline !important;
        margin-left: 0;
        width: initial;

        &.loading {
          animation: blink 0.8s ease-in-out infinite;
        }
      }

      @keyframes blink {
        0%, 100% {
          opacity: 1;
        }
        50% {
          opacity: 0;
        }
      }
    }

    .total {
      font-weight: 600;
      font-size: 15px;
      color: #269809;
    }

    .spinner-loading {
      display: inline-block;

      .vue-simple-spinner {
        width: 14px !important;
        height: 14px !important;
      }
    }
  }

  .description-data {
    display: block;
    min-height: 0px;
    height: 0px;
    padding: 0 7px 0 15px;
    overflow: hidden;
    opacity: 0;
    transition: all 0.2s ease;
    margin: 0 7px 0 21.5px;
    border-top-left-radius: 0;
    border-top-right-radius: 2px;
    border-bottom-left-radius: 2px;
    border-bottom-right-radius: 2px;

    .slider {
      padding: 0 20px 0 0;
    }

    .label {
      font-size: 14px;
      color: #444;
      font-weight: 400;
      letter-spacing: 0;
      text-align: left;
      display: block;
    }

    .label {
      i {
        margin-right: 5px;
        font-size: 13px;
        vertical-align: baseline;
        width: 15px;
      }
    }

    .radio-wrapper {
      .operation-info {
        display: inline;

        i {
          font-size: 10px;
          color: #076cfa6c;
          cursor: pointer;
          vertical-align: baseline;
          margin-left: 2px;
          text-indent: initial;
          transition: all 0.4s ease;
        }

        .tooltip {
          left: 3px !important;

          .tooltip-arrow {
            border-color: #2b83ff !important;
            border-left-color: transparent !important;
            border-top-color: transparent !important;
            border-bottom-color: transparent !important;
          }

          .tooltip-inner {
            background-color: #2b83ff !important;
            text-indent: initial;
          }
        }
      }

      .pretty {
        label {
          font-size: 14px;

          &:before,
          &:after {
            top: calc((0% - (100% - 1em)) - 2%) !important;
          }
        }

        &:hover {
          .operation-info {
            i {
              color: #2b83ff;
            }
          }
        }
      }
    }
  }

  &.description-open {
    .active-description {
      color: #076cfabe;
      background-color: #e4fffe;
      border-top-left-radius: 2px;
      border-top-right-radius: 2px;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      border: 1px dotted #999 !important;
      border-bottom: 0 !important;

      i {
        transform: scaleY(-1);
      }
    }

    .description-data {
      min-height: 85px;
      padding: 10px 10px 10px 15px;
      overflow: initial;
      opacity: 1;
      border: 1px dotted #999;
      background-color: #e4fffe62;
      margin-bottom: 5px;
      margin-right: 4px;
      height: auto;

      &.show-responses {
        min-height: 345px;
      }
      &.show-text-value,
      &.show-number-value,
      &.show-date-value {
        min-height: 147px;
      }

      .section-value {
        margin-top: 40px;
      }

      .input-value {
        border: 0;
        outline: none;
        font-family: 'Source Sans Pro';
        padding: 5px 10px 5px 10px;
        border: 1px solid #ddd;
        width: 100%;
        -webkit-transition: border 0.2s ease;
        transition: border 0.2s ease;

        &:hover,
        &:focus {
          border: 1px solid #999;
        }

        &.half {
          width: 49%;
        }

        &::placeholder {
          color: #adadad;
          font-style: italic;
        }
      }
    }
  }

  .edition-element {
    right: 0;
    top: 0;
    bottom: 0;
    padding: 10px 14px 15px 10px;
    height: 92px;
  }
}
</style>
